import { useState, type ReactNode } from "react";
import { Loader2, Share2 } from "lucide-react";
import { useFetcher } from "~/hooks/useFetcher";
import { ConversationProvider } from "~/pages/manifold-nextgen/components/conversations/ConversationContext";
import { DownloadDialog } from "~/pages/manifold-nextgen/components/conversations/artifacts/DownloadDialog";
import {
  ArtifactLineagePanel,
  DownloadHeaderButton,
  LineageStatusButton,
  LineageStatusRow,
} from "~/pages/manifold-nextgen/components/conversations/artifacts/lineage/ArtifactLineagePanel";
import { recipeErrorMessage } from "~/pages/manifold-nextgen/components/conversations/artifacts/lineage/recipeErrorMessage";
import { useArtifactPreview } from "~/pages/manifold-nextgen/components/conversations/artifacts/useArtifactPreview";
import { artifactRecipePath, type ArtifactPreview } from "~/services/artifacts";
import type { WorkflowSpec } from "~/services/pinnedArtifacts";
import { PinnedArtifactWorkflow } from "./PinnedArtifactWorkflow";
import { useSessionArtifactsScope, type SessionArtifactsScopeValue } from "./SessionArtifactsScope";
import { SessionContextDetailHeader } from "./SessionContextDetailHeader";
import { artifactLabel } from "./artifactLabel";
import { useArtifactSharing, type ArtifactSharing } from "./useArtifactSharing";

const NOOP = () => {};

interface SessionContextArtifactDetailProps {
  artifactName: string;
  onBack: () => void;
  onCollapse: () => void;
}

/** One session artifact's lineage in the session context sidebar.
 *
 * Opened from a card's Lineage button or the sidebar's Artifacts section,
 * which exist only inside a session, so the session scope is required.
 */
export const SessionContextArtifactDetail = ({
  artifactName,
  onBack,
  onCollapse,
}: SessionContextArtifactDetailProps) => {
  const scope = useSessionArtifactsScope();
  if (!scope) {
    throw new Error("SessionContextArtifactDetail must be rendered inside a session");
  }

  return (
    <ConversationProvider conversationId={scope.conversationId}>
      <ArtifactDetailHeader
        scope={scope}
        artifactName={artifactName}
        onBack={onBack}
        onCollapse={onCollapse}
      />
      <div className="flex-1 overflow-y-auto">
        <ArtifactDetailBody key={artifactName} scope={scope} artifactName={artifactName} />
      </div>
    </ConversationProvider>
  );
};

// Reads the same SWR entries as the body below, so naming costs no request.
const ArtifactDetailHeader = ({
  scope,
  artifactName,
  onBack,
  onCollapse,
}: {
  scope: SessionArtifactsScopeValue;
  artifactName: string;
  onBack: () => void;
  onCollapse: () => void;
}) => {
  const { preview } = useArtifactPreview(
    { conversationId: scope.conversationId, shareToken: null },
    artifactName
  );
  const sharing = useArtifactSharing(scope.projectUuid, scope.conversationId, scope.canShare);
  const label = artifactLabel(artifactName, preview, sharing.pinFor(artifactName));
  return (
    <SessionContextDetailHeader
      entityName="Artifacts"
      onBack={onBack}
      onCollapse={onCollapse}
      views={[{ value: "overview", label }]}
      currentView="overview"
      onSetView={NOOP}
    />
  );
};

const ArtifactDetailBody = ({
  scope,
  artifactName,
}: {
  scope: SessionArtifactsScopeValue;
  artifactName: string;
}) => {
  const { preview, error, isLoading, notFound } = useArtifactPreview(
    { conversationId: scope.conversationId, shareToken: null },
    artifactName
  );
  const sharing = useArtifactSharing(scope.projectUuid, scope.conversationId, scope.canShare);
  const [downloadOpen, setDownloadOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2
          className="size-5 animate-spin text-muted-foreground"
          aria-label="Loading artifact"
        />
      </div>
    );
  }
  if (error || notFound || !preview) {
    return (
      <p className="p-4 text-xs text-destructive">
        {notFound ? "This artifact is no longer in the session." : "Couldn't load this artifact."}
      </p>
    );
  }

  const pin = sharing.pinFor(artifactName);
  const toggle = sharing.toggle;
  const headerActions = <DownloadHeaderButton onClick={() => setDownloadOpen(true)} />;

  return (
    <>
      {pin ? (
        <PinnedArtifactWorkflow
          pin={pin}
          summary={preview.summary}
          artifactType={preview.artifact_type}
          headerActions={headerActions}
          onUnshare={toggle ? () => void toggle(artifactName, pin.display_name) : undefined}
          source="this session"
        />
      ) : (
        <UnsharedArtifactLineage
          scope={scope}
          preview={preview}
          sharing={sharing}
          headerActions={headerActions}
        />
      )}
      <DownloadDialog
        open={downloadOpen}
        onClose={() => setDownloadOpen(false)}
        artifactId={preview.artifact_id}
        artifactType={preview.artifact_type}
      />
    </>
  );
};

// An artifact not yet shared has no frozen recipe, so its lineage is derived
// from the session as it stands.
const UnsharedArtifactLineage = ({
  scope,
  preview,
  sharing,
  headerActions,
}: {
  scope: SessionArtifactsScopeValue;
  preview: ArtifactPreview;
  sharing: ArtifactSharing;
  headerActions: ReactNode;
}) => {
  const name = preview.artifact_name ?? preview.artifact_id;
  const label = artifactLabel(name, preview, null);
  const {
    data: recipe,
    error,
    isLoading,
  } = useFetcher<WorkflowSpec>(artifactRecipePath(scope.conversationId, preview.artifact_id));
  const toggle = sharing.toggle;

  return (
    <ArtifactLineagePanel
      name={label}
      // The label is usually the summary; don't print it twice.
      summary={preview.summary === label ? null : preview.summary}
      artifactType={preview.artifact_type}
      preview={preview}
      headerActions={headerActions}
      statusLine="Not shared · only in this session"
      recipe={recipe}
      isLoading={isLoading}
      recipeError={error ? recipeErrorMessage(error) : null}
      status={
        <LineageStatusRow
          action={
            toggle && (
              <LineageStatusButton
                onClick={() => void toggle(name, preview.summary ?? name)}
                disabled={!sharing.isReady}
              >
                <Share2 className="size-3.5" aria-hidden />
                Share
              </LineageStatusButton>
            )
          }
          title="Not shared · only in this session"
          detail={
            toggle
              ? "Sharing adds it to the project's Artifacts, where members can refresh it"
              : "Project owners can share it to the project's Artifacts"
          }
        />
      }
    />
  );
};
